import React, { createContext, useContext, useRef, useState } from 'react';

import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

type TutorialContextType = {
  isVisibleForChat: boolean;
  headerOverlayActiveForChat: boolean;
  setHeaderOverlayActiveForChat: React.Dispatch<React.SetStateAction<boolean>>;
  setIsVisibleForChat: React.Dispatch<React.SetStateAction<boolean>>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  startTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  closeTutorial: () => void;

  chatTutorialState: string | null;
  addMembersTutorialStateForChat: string | null;
  goToStep: (stepIndex: number) => void;
  setChatTutorialStateAndInStorage: (state: string) => void;
  setAddMemberTutorialStateAndInStorageForChat: (state: string) => void;
};

const ChatTutorialContext = createContext<TutorialContextType | undefined>(
  undefined
);

export const ChatTutorialProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isVisibleForChat, setIsVisibleForChat] = useState(false);
  const { trackEvent } = useMixpanel();
  const hasTrackedOnboardingStart = useRef(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [headerOverlayActiveForChat, setHeaderOverlayActiveForChat] =
    useState<boolean>(false);
  const [addMembersTutorialStateForChat, setAddMembersTutorialStateForChat] =
    useState('1');

  const [chatTutorialState, setChatTutorialState] = useState<string | null>('');

  const setChatTutorialStateAndInStorage = async (state: string) => {
    const previousState = chatTutorialState;
    // Set React state first so consumers see the new value synchronously.
    // Mirrors the fix in useChatTutorialWeb.tsx; see TA-9532 for context on
    // the async-first race it caused (stale-closure rollback in the web
    // chat tutorial). Applied here too so the mobile setter stays in sync
    // with the web one.
    setChatTutorialState(state);
    await AsyncStorage.setItem('chatTutorialState', state);

    if (previousState !== state) {
      if (
        !hasTrackedOnboardingStart.current &&
        (previousState === '' || previousState === null) &&
        state === '0'
      ) {
        hasTrackedOnboardingStart.current = true;
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_STARTED, {
          platform: 'mobile',
          tutorial_type: 'chat',
        });
      }

      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
        platform: 'mobile',
        tutorial_type: 'chat',
        from_state: previousState,
        to_state: state,
      });

      if (state === '9') {
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
          platform: 'mobile',
          tutorial_type: 'chat',
          final_state: state,
        });
      }
    }
  };

  const setAddMemberTutorialStateAndInStorageForChat = async (
    state: string
  ) => {
    await AsyncStorage.setItem('addMembersTutorialStateForChat', state);
    setAddMembersTutorialStateForChat(state);
  };

  const startTutorial = () => {
    setCurrentStep(0);
  };

  const nextStep = () => {
    setCurrentStep((step) => step + 1);
  };

  const previousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const closeTutorial = () => {
    setCurrentStep(0);
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0) setCurrentStep(stepIndex);
  };

  return (
    <ChatTutorialContext.Provider
      value={{
        isVisibleForChat,
        headerOverlayActiveForChat,
        setHeaderOverlayActiveForChat,
        addMembersTutorialStateForChat,
        setAddMemberTutorialStateAndInStorageForChat,
        setIsVisibleForChat,
        currentStep,
        setCurrentStep,
        startTutorial,
        nextStep,
        previousStep,
        closeTutorial,
        setChatTutorialStateAndInStorage,
        goToStep,
        chatTutorialState,
      }}>
      {children}
    </ChatTutorialContext.Provider>
  );
};

// Hook to use context
export function useChatTutorial() {
  const context = useContext(ChatTutorialContext);
  if (context === undefined) {
    throw new Error(
      'useChatTutorial must be used within a ChatTutorialProvider'
    );
  }
  return context;
}
